import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';

export interface MenuItem {
  id: number;
  menu_name: string;
  url?: string | null;
  sub_menus?: MenuItem[];
  expanded?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {

  menus: MenuItem[] = [];
  currentUrl = '';

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.loadMenus();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.expandOnlyActivePath(this.menus);
      });
  }

  /* ================= LOAD MENUS ================= */

  loadMenus(): void {
    const storedMenus = localStorage.getItem('auth_data');
    let data: MenuItem[] = [];

    try {
      data = storedMenus
        ? JSON.parse(storedMenus)?.permissions?.menu_tree || []
        :  [];
    } catch {
      data = [];
    }

    this.menus = this.prepareMenus(data);
    this.expandOnlyActivePath(this.menus);
  }

  prepareMenus(items: MenuItem[]): MenuItem[] {
    return items.map(menu => ({
      ...menu,
      expanded: false,
      sub_menus: menu.sub_menus?.length
        ? this.prepareMenus(menu.sub_menus)
        : []
    }));
  }

  /* ================= CORE LOGIC ================= */

  expandOnlyActivePath(menus: MenuItem[]): boolean {
    let foundActive = false;

    menus.forEach(menu => {
      // Reset first ❗
      menu.expanded = false;

      let childActive = false;

      // Direct match
      if (menu.url && this.currentUrl.startsWith(menu.url)) {
        menu.expanded = true;
        foundActive = true;
      }

      // Check children
      if (menu.sub_menus?.length) {
        childActive = this.expandOnlyActivePath(menu.sub_menus);
        if (childActive) {
          menu.expanded = true; // 🔥 expand only active path
          foundActive = true;
        }
      }
    });

    return foundActive;
  }

  /* ================= UI HANDLERS ================= */

  toggleMenu(menu: MenuItem): void {
    // ❌ Do not collapse active path
    if(menu.expanded) {
      menu.expanded = false;
      return;
    }
    // if (this.isMenuInActivePath(menu)) return;
    // Collapse all siblings
    this.collapseAll(this.menus);
    menu.expanded = true;
  }

  collapseAll(menus: MenuItem[]): void {
    menus.forEach(menu => {
      menu.expanded = false;
      if (menu.sub_menus?.length) {
        this.collapseAll(menu.sub_menus);
      }
    });
  }

  isMenuInActivePath(menu: MenuItem): boolean {
    if (menu.url && this.currentUrl.startsWith(menu.url)) return true;
    return menu.sub_menus?.some(child => this.isMenuInActivePath(child)) ?? false;
  }

  hasSubMenus(menu: MenuItem): boolean {
    return !!menu.sub_menus && menu.sub_menus.length > 0;
  }
}
